import * as React from 'react'
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import TelegramIcon from '@mui/icons-material/Telegram'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CopiedSnackbar from '../../../../components/Snackbar/CopiedSnackbar'

type Step = {
  id: number
  text: React.ReactNode
}

export interface ConnectionInstructionsProps {
  code: string
  botHandle?: string
  title?: string
  onCopy?: (code: string) => void
  sx?: object
}

export default function ConnectionInstructions({
  code,
  botHandle = '@celetus_bot',
  title = '',
  onCopy,
  sx,
}: ConnectionInstructionsProps) {
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)

  console.log({ code })

  const steps: Step[] = [
    {
      id: 1,
      text: (
        <>
          Se você ainda não possui um grupo ou canal no Telegram, crie um
          diretamente pelo aplicativo.
        </>
      ),
    },
    {
      id: 2,
      text: (
        <>
          Nas configurações do seu grupo ou canal, acesse{' '}
          <strong>Administradores &gt; Adicionar Admin.</strong>
        </>
      ),
    },
    {
      id: 3,
      text: (
        <>
          Busque por <strong>{botHandle}</strong>, conceda todas as permissões e
          confirme.
        </>
      ),
    },
    {
      id: 4,
      text: (
        <>Volte para o seu grupo, e envie uma mensagem com o código abaixo:</>
      ),
    },
  ]

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setOpen(true)
      onCopy?.(code)
    } catch {
      const txt = document.createElement('textarea')
      txt.value = code
      document.body.appendChild(txt)
      txt.select()
      document.execCommand('copy')
      document.body.removeChild(txt)
      setOpen(true)
      onCopy?.(code)
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        maxWidth: 720,
        ...sx,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: theme.palette.info.light,
            color: theme.palette.info.contrastText,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <TelegramIcon />
        </Box>
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
      </Stack>

      <Box>
        {steps.map((s, i) => (
          <Stack
            key={s.id}
            direction="row"
            spacing={2}
            alignItems="flex-start"
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 28,
                display: 'flex',
                justifyContent: 'center',
                pt: 0.5,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 34,
                  bottom: i === steps.length - 1 ? 0 : -32,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  borderLeft: `2px dotted ${theme.palette.divider}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: theme.palette.grey[100],
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 600,
                }}
              >
                {s.id}
              </Box>
            </Box>

            <Box flex={1} pb={2}>
              <Typography variant="body1">{s.text}</Typography>

              {s.id === 4 && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  mt={2}
                >
                  <Chip
                    label={code}
                    sx={{
                      fontWeight: 700,
                      bgcolor: theme.palette.grey[100],
                      height: 44,
                      borderRadius: '24px',
                      px: 1.5,
                      fontSize: 16,
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  />
                  <Button
                    onClick={handleCopy}
                    variant="contained"
                    size="large"
                    startIcon={<ContentCopyIcon />}
                    sx={{
                      minWidth: 140,
                      borderRadius: 999,
                      textTransform: 'none',
                    }}
                  >
                    Copiar
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>
        ))}
      </Box>

      <CopiedSnackbar open={open} onClose={() => setOpen(false)} />
    </Paper>
  )
}
